import { useBunja } from 'bunja/react';
import { useAtom } from 'jotai';

import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import { Slider } from '@flipster/ui';
import segment from '@flipster/utils/segment';
import { PositionEvent } from '@flipster/utils/segment/events/position';

import { getLeverageMarks } from '@web/constants/slider-marker';

import { adjustLeverageModalBunja } from '../state';
import { CrossLeverageNudge } from './cross-leverage-nudge';
import { LeverageFeedback } from './leverage-feedback';
import { LeverageInput } from './leverage-input';
import { LeverageStepper } from './leverage-stepper';

/** Tighter than Slider default (0.02) so 2000x ceiling is selectable without ±40 quantization. */
const leverageSliderSnapThresholdRatio = 0.002;

interface LeverageControlsProps {
  symbol: string;
  modeMaxLeverage: number;
}

/** Input + stepper + slider + cross nudge + error/stats feedback inside the bordered card. */
export const LeverageControls = ({ symbol, modeMaxLeverage }: LeverageControlsProps) => {
  const { leverageAtom } = useBunja(adjustLeverageModalBunja);
  const [leverage, setLeverage] = useAtom(leverageAtom);

  const handleChangeSlider = (newLeverage: number) => {
    const leverage = newLeverage === 0 ? 1 : newLeverage;
    setLeverage(leverage);

    segment.track(PositionEvent.AdjustLeverageRangeClick, {
      symbol,
      leverage_option: leverage,
    });
  };

  return (
    <div
      className={css({
        px: 20,
        py: 16,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'border.default.level-1',
        rounded: 12,
      })}
    >
      <div
        className={flex({
          justify: 'space-between',
          align: 'center',
          py: '12px',
        })}
      >
        <div className={css({ position: 'relative', textStyle: 'Title/M/Monospace', flex: 1 })}>
          <LeverageInput max={modeMaxLeverage} symbol={symbol} />
        </div>
        <div className={flex({ gap: 8 })}>
          <LeverageStepper max={modeMaxLeverage} symbol={symbol} />
        </div>
      </div>
      <div className={css({ pt: 24 })}>
        <Slider
          value={leverage}
          min={1}
          max={modeMaxLeverage}
          marks={getLeverageMarks(modeMaxLeverage)}
          onChange={handleChangeSlider}
          hierarchy='secondary'
          snapThresholdRatio={leverageSliderSnapThresholdRatio}
          marksStyle={css({
            textStyle: 'Text/M/Regular!',
            color: 'content.default.level-5!',
            whiteSpace: 'nowrap',
          })}
        />
      </div>
      <div className={css({ mt: 30, h: 16, mb: 8 })}>
        <CrossLeverageNudge />
      </div>
      <LeverageFeedback />
    </div>
  );
};
